import React, { useState, useEffect } from 'react';
import {
    IMenuItem,
    mainMenuTypes,
    PasslyDivider,
    ModalComponent,
    ModalActionsComponent,
    PasslyCheckbox,
    PasslyTextField
} from '@app/components';
import { getMenuItemById, localStore, genericErrorString, defaultOrgId } from '@app/helpers';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { useDispatch } from 'react-redux';
import { IListPageBarEnabled } from '@app/models';
import { setExceptionHandler } from '@app/store/actions';
import { Link, FormControl, createMuiTheme, MuiThemeProvider } from '@material-ui/core';
import { AddRolesNoUserIdDialog } from '@app/pages/directoryManager/dialogs/addRolesNoUserId.dialog';
import { AddTenantsDialog } from '@app/pages/directoryManager/dialogs/addTenants.dialog';
import { confirm } from '@app/components/Modal/confirmModal';
import { notificationService, basicService } from '@app/services';
import { AxiosError } from 'axios';
import MUIRichTextEditor from 'mui-rte';
import { stateToHTML } from 'draft-js-export-html';

interface ITenantModel {
    TenantId: string;
    HomeRealm: string;
}

interface IRoleModel {
    RoleId: string;
    DisplayName: string;
}

const NotificationsPage = () => {
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Notifications);

    const [menuItem] = useState<IMenuItem | undefined>(item);

    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [allTenants, setAllTenants] = useState<boolean>(true);
    const [tenantsToSendTo, setTenantsToSendTo] = useState<ITenantModel[]>([]);
    const [rolesToSendTo, setRolesToSendTo] = useState<IRoleModel[]>([]);
    const [subject, setSubject] = useState<string>('');
    const [subjectInvalid, setSubjectInvalid] = useState<boolean>(false);
    const [sendByEmail, setSendByEmail] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [formInvalid, setFormInvalid] = useState<boolean>(true);

    const [openRolesDialog, setOpenRolesDialog] = useState<boolean>(false);
    const [openTenantsDialog, setOpenTenantsDialog] = useState<boolean>(false);

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: false,
        organizationsEnabled: false,
        actionMenuEnabled: false
    };

    useSetPage(setListPageBarEnabledOptions, menuItem);

    const defaultTheme = createMuiTheme();
    Object.assign(defaultTheme, {
        overrides: {
            MUIRichTextEditor: {
                root: {
                    backgroundColor: '#ebebeb',
                    marginTop: 20,
                    height: '350px',
                    maxHeight: '350px',
                    overflow: 'auto'
                },
                editor: {
                    backgroundColor: '#ebebeb'
                },
                toolbar: {
                    borderBottom: '1px solid gray',
                    backgroundColor: '#ebebeb'
                }
            }
        }
    });

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        // get tenants from local storage if they exist
        const tenantsToSendTo: string | null = localStore.getItem('tenantNotificationReceivers');

        if (tenantsToSendTo !== null) {
            const json: any = JSON.parse(tenantsToSendTo);
            const tenantArray: ITenantModel[] = [];

            if (json && json.Tenants.length > 0) {
                json.Tenants.forEach(function (tenant: any) {
                    let t: ITenantModel = {} as ITenantModel;
                    t.TenantId = tenant.TenantId;
                    t.HomeRealm = tenant.HomeRealm;

                    tenantArray.push(t);
                });

                setTenantsToSendTo(tenantArray);
                setAllTenants(false);
            }

            localStore.removeItem('tenantNotificationReceivers');
        }
    }, []);

    useEffect(() => {
        if ((tenantsToSendTo.length === 0 && !allTenants) || subject.trim() === '' || rolesToSendTo.length === 0 || (message === '' || message === '<p><br></p>')) {
            setFormInvalid(true);
        }
        else {
            setFormInvalid(false);
        }
    }, [subject, rolesToSendTo, message, tenantsToSendTo, allTenants]);

    const removeTenantFromList = (tenant: ITenantModel) => {
        let newTenantList: ITenantModel[] = tenantsToSendTo.filter((t: ITenantModel) => {
            return t.TenantId !== tenant.TenantId;
        });

        setTenantsToSendTo(newTenantList);
    };

    const handleAllTenantsCheck = () => {
        setAllTenants(!allTenants);
    };

    const handleSendByEmailCheck = () => {
        setSendByEmail(!sendByEmail);
    };

    const removeRoleFromList = (role: IRoleModel) => {
        let newRoleList: IRoleModel[] = rolesToSendTo.filter((t: IRoleModel) => {
            return t.RoleId !== role.RoleId;
        });

        setRolesToSendTo(newRoleList);
    };

    const onSubjectChange = (e: any) => {
        const { name, value } = e.target;

        setSubject(value);

        if (value.trim() === '') {
            setSubjectInvalid(true);
        }
    };

    const handleModalOpenCallbackRoles = (results?: any) => {
        if (results !== undefined) {
            let newRoles = [...rolesToSendTo];

            results.forEach((newRole: any) => {
                if (!rolesToSendTo.some(e => e.RoleId === newRole.id)) {
                    let r: IRoleModel = {} as IRoleModel;
                    r.RoleId = newRole.id;
                    r.DisplayName = newRole.name;

                    newRoles.push(r);
                }
            });

            setRolesToSendTo(newRoles);
        }

        setOpenRolesDialog(!openRolesDialog);
    };

    const handleModalRoles = () => {
        setOpenRolesDialog(!openRolesDialog);
    };

    const handleModalOpenCallbackTenants = (results?: any) => {
        if (results !== undefined) {
            let newTenants = [...tenantsToSendTo];

            results.forEach((newTenant: any) => {
                if (!tenantsToSendTo.some(e => e.TenantId === newTenant.id)) {
                    let t: ITenantModel = {} as ITenantModel;
                    t.TenantId = newTenant.id;
                    t.HomeRealm = newTenant.name;

                    newTenants.push(t);
                }
            });

            setTenantsToSendTo(newTenants);
        }

        setOpenTenantsDialog(!openTenantsDialog);
    };

    const handleModalTenants = () => {
        setOpenTenantsDialog(!openTenantsDialog);
    };

    const handleSend = () => {
        let messageBody: string = translation('TENANT_NOTIFICATION_MESSAGE');

        let roles: string[] = [];

        rolesToSendTo.forEach(function (role: IRoleModel) {
            messageBody += `<li>${role.DisplayName}</li>`;
            roles.push(role.DisplayName);
        });

        confirm(messageBody,
            translation('OK'),
            translation('CANCEL'),
            translation('CONFIRM_SEND_TENANT_NOTIFICATION'),
            true).then(
                () => {
                    let tenants: string[] = [];

                    if (!allTenants) {
                        tenantsToSendTo.forEach(function (tenant: ITenantModel) {
                            tenants.push(tenant.TenantId);
                        });
                    }

                    notificationService.notifyTenant(message, subject, tenants, roles, sendByEmail, handleError, handleSuccess);
                },
                () => {
                    // do nothing on cancel
                }
            );
    };

    const handleError = (response: AxiosError<any>) => {
        setErrorMessage(genericErrorString(response));
    };

    const handleSuccess = () => {
        confirm(translation('SUCCESSFULLY_SENT_NOTIFICATION_MESSAGE'),
            translation('SEND_ANOTHER'),
            translation('OK'),
            translation('SUCCESSFULLY_SENT_TENANT_NOTIFICATION')).then(
                () => {
                    // they want to send another one so reset the form
                    basicService.redirect('/system/notifications');
                },
                () => {
                    // do nothing on cancel ('ok' in this case)
                }
            );
    };

    const handleMessageChange = (event: any) => {
        setMessage(stateToHTML(event.getCurrentContent()));
    };

    return (
        <>
            <ModalComponent
                headerTitle={translation('ADD_TENANTS')}
                modalSwitcherCallback={handleModalTenants}
                form={<AddTenantsDialog sendBackResultsFunction={handleModalOpenCallbackTenants} closeDialogFunction={handleModalTenants} />}
                localModalSwitcher={openTenantsDialog}
            />

            <ModalComponent
                headerTitle={translation('ADD_ROLES')}
                modalSwitcherCallback={handleModalRoles}
                form={<AddRolesNoUserIdDialog sendBackResultsFunction={handleModalOpenCallbackRoles} closeDialogFunction={handleModalRoles} orgId={defaultOrgId} />}
                localModalSwitcher={openRolesDialog}
            />

            <div className='content'>
                <div>
                    <span>{translation('MSG_TO')}&nbsp;</span>

                    {allTenants ?
                        <>
                            <span className='notification__toRecipients'>{translation('ALL_TENANTS')}</span>
                        </>
                        :
                        <>
                            <span style={{ overflowWrap: 'break-word'}}>
                                {tenantsToSendTo.map((tenant: ITenantModel, index: number) => (
                                    <>
                                        <span className='notification__toRecipients'>
                                            {tenant.HomeRealm}&nbsp;
                                            <Link onClick={() => removeTenantFromList(tenant)}>&times;</Link>
                                        </span>
                                    </>
                                ))
                                }
                                <span>
                                    <Link className='notification__plusSign' variant='h3' onClick={() => handleModalTenants()}>+</Link>
                                </span>
                            </span>
                        </>
                    }

                </div>
                <div>
                    <PasslyDivider />
                </div>
                <div className='notification__spaceBetweenRows'>
                    <PasslyCheckbox
                        name='allTenantCheckbox'
                        checked={allTenants}
                        onChange={handleAllTenantsCheck}
                        label={translation('SEND_TO_ALL_TENANTS')}
                    />
                </div>
                <div>
                    <span>{translation('IN_ROLES')}&nbsp;</span>
                    <span>
                        {rolesToSendTo.map((role: IRoleModel, index: number) => (
                            <>
                                <span className='notification__toRecipients'>
                                    {role.DisplayName}&nbsp;
                                     <Link onClick={() => removeRoleFromList(role)}>&times;</Link>
                                </span>

                            </>
                        ))
                        }
                        <span>
                            <Link className='notification__plusSign' variant='h3' onClick={() => handleModalRoles()}>+</Link>
                        </span>
                    </span>
                </div>
                <div className='notification__spaceBetweenRows'>
                    <PasslyDivider />
                </div>
                <div>
                    <FormControl fullWidth>
                        <PasslyTextField
                            name='subject'
                            label={'SUBJECT'}
                            value={subject}
                            onChange={onSubjectChange}
                            id='subject-input'
                            fullWidth
                            autoFocus
                            required
                            error={subjectInvalid}
                            helperText={subjectInvalid && translation('REQUIRED')}
                        />
                    </FormControl>
                </div>
                <div>
                    <PasslyDivider />
                </div>
                <div>
                    <MuiThemeProvider theme={defaultTheme}>
                        <MUIRichTextEditor
                            id='messageEditor'
                            label={translation('ENTER_MESSAGE')}
                            controls={['title', 'bold', 'italic', 'undo', 'redo', 'numberList', 'bulletList']}
                            onChange={handleMessageChange}
                        />
                    </MuiThemeProvider>
                </div>
                <div>
                    <PasslyCheckbox
                        name='sendByEmailCheckbox'
                        checked={sendByEmail}
                        onChange={handleSendByEmailCheck}
                        label={translation('NOTIFY_ALSO_BY_EMAIL')}
                    />
                </div>
            </div>

            {errorMessage !== undefined && errorMessage !== '' && <div className='modal__error'>{errorMessage}</div>}

            <ModalActionsComponent
                primaryBtn={translation('SEND')}
                onClickPrimary={handleSend}
                disabled={formInvalid}
                id='sendNotfication'
            />
        </>
    );

};

export default NotificationsPage;
